import { useState } from "react";
import { Calculator, Lightning, Flame, CaretRight } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ── Electrical Calculators ──

function OhmsLaw() {
  const [v, setV] = useState(""); const [i, setI] = useState(""); const [r, setR] = useState(""); const [p, setP] = useState("");
  const calc = (known) => {
    const vv = parseFloat(v), ii = parseFloat(i), rr = parseFloat(r);
    if (known === "vi" && vv && ii) { setR((vv / ii).toFixed(2)); setP((vv * ii).toFixed(2)); }
    else if (known === "vr" && vv && rr) { setI((vv / rr).toFixed(3)); setP(((vv * vv) / rr).toFixed(2)); }
    else if (known === "ir" && ii && rr) { setV((ii * rr).toFixed(2)); setP((ii * ii * rr).toFixed(2)); }
  };
  return (
    <div className="wp-card" data-testid="ohms-law-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Ohm's Law / Power</h3>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {[{l:"Voltage (V)",v:v,s:setV},{l:"Current (A)",v:i,s:setI},{l:"Resistance (ohm)",v:r,s:setR},{l:"Power (W)",v:p,s:setP}].map(f=>(
          <div key={f.l}><label className="text-[10px] font-mono-data text-[#404040]">{f.l}</label>
          <input type="number" value={f.v} onChange={e=>f.s(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        ))}
      </div>
      <div className="flex gap-1">
        {[["V+I","vi"],["V+R","vr"],["I+R","ir"]].map(([l,k])=>(
          <button key={k} onClick={()=>calc(k)} className="flex-1 py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] transition-colors">Calc ({l})</button>
        ))}
      </div>
    </div>
  );
}

function VoltageDrop() {
  const [amps, setAmps] = useState(""); const [length, setLength] = useState(""); const [gauge, setGauge] = useState("12"); const [voltage, setVoltage] = useState("120"); const [result, setResult] = useState(null);
  const RESISTANCE = {"14":3.14,"12":1.98,"10":1.24,"8":0.778,"6":0.491,"4":0.308,"3":0.245,"2":0.194,"1":0.154,"1/0":0.122,"2/0":0.0967,"3/0":0.0766,"4/0":0.0608};
  const calc = () => {
    const a=parseFloat(amps),l=parseFloat(length),v=parseFloat(voltage),r=RESISTANCE[gauge];
    if(!a||!l||!v||!r) return;
    const drop = 2 * l * r * a / 1000; const pct = (drop/v)*100;
    setResult({drop:drop.toFixed(2),pct:pct.toFixed(1),ok:pct<=3});
  };
  return (
    <div className="wp-card" data-testid="voltage-drop-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Voltage Drop</h3>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><label className="text-[10px] font-mono-data text-[#404040]">AMPS</label><input type="number" value={amps} onChange={e=>setAmps(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">ONE-WAY LENGTH (FT)</label><input type="number" value={length} onChange={e=>setLength(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">WIRE GAUGE (AWG)</label>
          <select value={gauge} onChange={e=>setGauge(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]">
            {Object.keys(RESISTANCE).map(g=><option key={g} value={g}>{g} AWG</option>)}
          </select></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">SYSTEM VOLTAGE</label>
          <select value={voltage} onChange={e=>setVoltage(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]">
            {["120","208","240","277","480"].map(v=><option key={v} value={v}>{v}V</option>)}
          </select></div>
      </div>
      <button onClick={calc} className="w-full py-2 text-xs font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C] transition-colors">Calculate Drop</button>
      {result && <div className={`mt-2 p-2 rounded-sm text-center ${result.ok?"bg-[#34C75910] border border-[#34C759]":"bg-[#FF5A0010] border border-[#FF5A00]"}`}>
        <span className="font-mono font-bold text-lg">{result.drop}V</span> <span className="font-mono-data text-[10px]">({result.pct}%)</span>
        <div className={`text-[10px] font-mono-data ${result.ok?"text-[#34C759]":"text-[#FF5A00]"}`}>{result.ok?"WITHIN 3% LIMIT":"EXCEEDS 3% - UPSIZE WIRE"}</div>
      </div>}
    </div>
  );
}

function WireAmpacity() {
  const data = [
    {gauge:"14",cu60:15,cu75:15,al75:"-"},{gauge:"12",cu60:20,cu75:20,al75:15},{gauge:"10",cu60:30,cu75:30,al75:25},
    {gauge:"8",cu60:40,cu75:50,al75:40},{gauge:"6",cu60:55,cu75:65,al75:50},{gauge:"4",cu60:70,cu75:85,al75:65},
    {gauge:"3",cu60:85,cu75:100,al75:75},{gauge:"2",cu60:95,cu75:115,al75:90},{gauge:"1",cu60:110,cu75:130,al75:100},
    {gauge:"1/0",cu60:125,cu75:150,al75:120},{gauge:"2/0",cu60:145,cu75:175,al75:135},{gauge:"3/0",cu60:165,cu75:200,al75:155},
    {gauge:"4/0",cu60:195,cu75:230,al75:180},
  ];
  return (
    <div className="wp-card" data-testid="wire-ampacity-table">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Wire Ampacity (NEC 310.16)</h3>
      <div className="overflow-x-auto text-[10px] font-mono-data">
        <table className="w-full"><thead><tr className="border-b border-[#0A0A0A]">
          <th className="text-left p-1">AWG</th><th className="p-1">Cu 60C</th><th className="p-1">Cu 75C</th><th className="p-1">Al 75C</th>
        </tr></thead><tbody>
          {data.map(r=><tr key={r.gauge} className="border-b border-[#E5E5E5]">
            <td className="p-1 font-bold">{r.gauge}</td><td className="p-1 text-center">{r.cu60}A</td><td className="p-1 text-center">{r.cu75}A</td><td className="p-1 text-center">{r.al75}A</td>
          </tr>)}
        </tbody></table>
      </div>
    </div>
  );
}

// ── HVAC Calculators ──

function BTUCalc() {
  const [sqft, setSqft] = useState(""); const [ceiling, setCeiling] = useState("8"); const [insulation, setInsulation] = useState("average"); const [climate, setClimate] = useState("moderate"); const [result, setResult] = useState(null);
  const calc = () => {
    const sf = parseFloat(sqft); if(!sf) return;
    let base = sf * 20; // base BTU per sqft
    const ceilFactor = parseFloat(ceiling) > 8 ? 1 + ((parseFloat(ceiling)-8)*0.05) : 1;
    const insFactor = {poor:1.3,average:1.0,good:0.85}[insulation]||1;
    const climFactor = {hot:1.2,moderate:1.0,cold:1.15}[climate]||1;
    const btu = Math.round(base * ceilFactor * insFactor * climFactor);
    const tons = (btu/12000).toFixed(1);
    setResult({btu,tons});
  };
  return (
    <div className="wp-card" data-testid="btu-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>BTU / Tonnage Calculator</h3>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><label className="text-[10px] font-mono-data text-[#404040]">SQUARE FOOTAGE</label><input type="number" value={sqft} onChange={e=>setSqft(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">CEILING HEIGHT (FT)</label><input type="number" value={ceiling} onChange={e=>setCeiling(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">INSULATION</label>
          <select value={insulation} onChange={e=>setInsulation(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm"><option value="poor">Poor</option><option value="average">Average</option><option value="good">Good</option></select></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">CLIMATE</label>
          <select value={climate} onChange={e=>setClimate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm"><option value="hot">Hot</option><option value="moderate">Moderate</option><option value="cold">Cold</option></select></div>
      </div>
      <button onClick={calc} className="w-full py-2 text-xs font-bold bg-[#FF5A00] text-white rounded-sm hover:bg-[#CC4800] transition-colors">Calculate BTU</button>
      {result && <div className="mt-2 p-2 bg-[#FF5A0010] border border-[#FF5A00] rounded-sm text-center">
        <span className="font-mono font-bold text-lg">{result.btu.toLocaleString()} BTU</span>
        <div className="font-mono-data text-[10px] text-[#404040]">{result.tons} TONS</div>
      </div>}
    </div>
  );
}

function DuctSizing() {
  const [cfm, setCfm] = useState(""); const [fpm, setFpm] = useState("900"); const [ductType, setDuctType] = useState("rigid"); const [result, setResult] = useState(null);
  const calc = () => {
    const c = parseFloat(cfm); const v = parseFloat(fpm); if(!c||!v) return;
    const area = c / v;
    const diameter = Math.sqrt((area * 4) / Math.PI) * 12;
    const flexAdj = ductType === "flex" ? 2 : 0;
    const adjDia = diameter + flexAdj;
    const rectW = Math.ceil(adjDia); const rectH = Math.ceil(area * 144 / rectW);
    setResult({diameter: diameter.toFixed(1), adjDiameter: adjDia.toFixed(1), area: (area*144).toFixed(0), rectW, rectH, isFlex: ductType==="flex"});
  };
  return (
    <div className="wp-card" data-testid="duct-sizing-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Duct Sizing</h3>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div><label className="text-[10px] font-mono-data text-[#404040]">AIRFLOW (CFM)</label>
          <input type="number" value={cfm} onChange={e=>setCfm(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">VELOCITY (FPM)</label>
          <input type="number" value={fpm} onChange={e=>setFpm(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
      </div>
      <div className="flex gap-2 mb-2">
        {[["rigid","Rigid/Sheet Metal"],["flex","Flex Duct"]].map(([k,l])=>(
          <button key={k} onClick={()=>setDuctType(k)} className={`flex-1 py-1.5 text-[10px] font-bold rounded-sm border transition-colors ${ductType===k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5]"}`}>{l}</button>
        ))}
      </div>
      <button onClick={calc} className="w-full py-2 text-xs font-bold bg-[#FF5A00] text-white rounded-sm hover:bg-[#CC4800] transition-colors">Calculate Size</button>
      {result && <div className="mt-2 p-2 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm">
        <div className="grid grid-cols-2 gap-2 text-center">
          <div><span className="font-mono-data text-[10px] text-[#404040]">ROUND</span><div className="font-mono font-bold">{result.isFlex ? result.adjDiameter : result.diameter}"</div>
            {result.isFlex && <div className="text-[8px] text-[#FF5A00] font-mono-data">+2" for flex</div>}</div>
          <div><span className="font-mono-data text-[10px] text-[#404040]">RECT</span><div className="font-mono font-bold">{result.rectW}" x {result.rectH}"</div></div>
        </div>
        <div className="text-center mt-1 font-mono-data text-[10px] text-[#404040]">{fpm} FPM | {result.area} sq in</div>
      </div>}
    </div>
  );
}

function RefrigerantChart() {
  const [selected, setSelected] = useState("R-410A");
  const allData = {
    "R-410A": {temps:[{f:30,lo:101,hi:243},{f:40,lo:118,hi:298},{f:50,lo:138,hi:361},{f:60,lo:160,hi:431},{f:70,lo:186,hi:509},{f:80,lo:215,hi:596},{f:90,lo:247,hi:692},{f:100,lo:283,hi:798},{f:110,lo:323,hi:915}],gwp:2088,status:"Phase-down",odp:0},
    "R-454B": {temps:[{f:30,lo:76,hi:200},{f:40,lo:90,hi:248},{f:50,lo:106,hi:303},{f:60,lo:124,hi:365},{f:70,lo:145,hi:435},{f:80,lo:168,hi:512},{f:90,lo:194,hi:598},{f:100,lo:224,hi:693},{f:110,lo:257,hi:799}],gwp:466,status:"Replacement",odp:0},
    "R-32": {temps:[{f:30,lo:80,hi:210},{f:40,lo:95,hi:262},{f:50,lo:112,hi:321},{f:60,lo:131,hi:388},{f:70,lo:153,hi:463},{f:80,lo:178,hi:547},{f:90,lo:206,hi:641},{f:100,lo:237,hi:745},{f:110,lo:272,hi:860}],gwp:675,status:"Alternative",odp:0},
    "R-22": {temps:[{f:30,lo:54,hi:137},{f:40,lo:68,hi:172},{f:50,lo:84,hi:211},{f:60,lo:102,hi:256},{f:70,lo:122,hi:306},{f:80,lo:145,hi:362},{f:90,lo:171,hi:424},{f:100,lo:200,hi:493},{f:110,lo:233,hi:569}],gwp:1810,status:"Banned",odp:0.05},
    "R-134a": {temps:[{f:30,lo:26,hi:90},{f:40,lo:35,hi:113},{f:50,lo:46,hi:139},{f:60,lo:57,hi:169},{f:70,lo:71,hi:203},{f:80,lo:87,hi:241},{f:90,lo:105,hi:284},{f:100,lo:126,hi:332},{f:110,lo:149,hi:385}],gwp:1430,status:"Phase-down",odp:0},
  };
  const ref = allData[selected];
  const statusColor = ref?.status==="Banned"?"#FF5A00":ref?.status==="Replacement"?"#34C759":"#404040";
  return (
    <div className="wp-card" data-testid="refrigerant-chart">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Refrigerant P/T Chart</h3>
      <div className="flex gap-1 mb-2 overflow-x-auto">
        {Object.keys(allData).map(r=>(
          <button key={r} onClick={()=>setSelected(r)} data-testid={`ref-${r}`}
            className={`px-2 py-1 text-[10px] font-bold rounded-sm border shrink-0 ${selected===r?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5] hover:border-[#0A0A0A]"}`}>{r}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-2 text-[10px] font-mono-data">
        <span style={{color:statusColor}}>{ref?.status}</span>
        <span className="text-[#404040]">GWP: {ref?.gwp}</span>
        <span className="text-[#404040]">ODP: {ref?.odp}</span>
      </div>
      <div className="overflow-x-auto text-[10px] font-mono-data">
        <table className="w-full"><thead><tr className="border-b border-[#0A0A0A]">
          <th className="text-left p-1">Temp (F)</th><th className="p-1">Low PSI</th><th className="p-1">High PSI</th>
        </tr></thead><tbody>
          {ref?.temps.map(t=><tr key={t.f} className="border-b border-[#E5E5E5]">
            <td className="p-1 font-bold">{t.f}</td><td className="p-1 text-center">{t.lo}</td><td className="p-1 text-center">{t.hi}</td>
          </tr>)}
        </tbody></table>
      </div>
    </div>
  );
}

function ConduitFill() {
  const [conduitSize, setConduitSize] = useState("3/4");
  const [wireGauge, setWireGauge] = useState("12");
  const [wireCount, setWireCount] = useState("");
  const [result, setResult] = useState(null);
  // Conduit area (sq in) for EMT
  const CONDUIT = {"1/2":0.304,"3/4":0.533,"1":0.864,"1-1/4":1.496,"1-1/2":2.036,"2":3.356};
  // Wire area (sq in) with insulation THHN
  const WIRE = {"14":0.0097,"12":0.0133,"10":0.0211,"8":0.0366,"6":0.0507,"4":0.0824,"3":0.0973,"2":0.1158,"1":0.1562};
  const calc = () => {
    const n=parseInt(wireCount); if(!n) return;
    const ca=CONDUIT[conduitSize]; const wa=WIRE[wireGauge]; if(!ca||!wa) return;
    const totalWire=wa*n; const fillPct=(totalWire/ca)*100;
    const maxPct = n===1?53:n===2?31:40; // NEC 1-wire=53%, 2=31%, 3+=40%
    setResult({fill:fillPct.toFixed(1),max:maxPct,ok:fillPct<=maxPct,totalArea:totalWire.toFixed(4),conduitArea:ca});
  };
  return (
    <div className="wp-card" data-testid="conduit-fill-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Conduit Fill (NEC Ch.9)</h3>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div><label className="text-[10px] font-mono-data text-[#404040]">EMT SIZE</label>
          <select value={conduitSize} onChange={e=>setConduitSize(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm">
            {Object.keys(CONDUIT).map(s=><option key={s} value={s}>{s}"</option>)}</select></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]">WIRE (THHN)</label>
          <select value={wireGauge} onChange={e=>setWireGauge(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm">
            {Object.keys(WIRE).map(g=><option key={g} value={g}>{g} AWG</option>)}</select></div>
        <div><label className="text-[10px] font-mono-data text-[#404040]"># WIRES</label>
          <input type="number" value={wireCount} onChange={e=>setWireCount(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
      </div>
      <button onClick={calc} className="w-full py-2 text-xs font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C]">Check Fill</button>
      {result && <div className={`mt-2 p-2 rounded-sm text-center ${result.ok?"bg-[#34C75910] border border-[#34C759]":"bg-[#FF5A0010] border border-[#FF5A00]"}`}>
        <span className="font-mono font-bold text-lg">{result.fill}%</span> <span className="font-mono-data text-[10px]">/ {result.max}% MAX</span>
        <div className={`text-[10px] font-mono-data ${result.ok?"text-[#34C759]":"text-[#FF5A00]"}`}>{result.ok?"WITHIN NEC LIMITS":"EXCEEDS FILL - UPSIZE CONDUIT"}</div>
      </div>}
    </div>
  );
}

function BoxFill() {
  const [boxType, setBoxType] = useState("4x4x1.5");
  const [w14, setW14] = useState("0"); const [w12, setW12] = useState("0"); const [w10, setW10] = useState("0");
  const [devices, setDevices] = useState("0"); const [grounds, setGrounds] = useState("1"); const [clamps, setClamps] = useState("0");
  const [result, setResult] = useState(null);
  const BOX_VOL = {"4x4x1.5":21,"4x4x2.125":30.3,"4-11/16x4-11/16x1.5":25.5,"4-11/16x4-11/16x2.125":42,"3x2x2.5":12.5,"3x2x3.5":18};
  const WIRE_VOL = {"14":2,"12":2.25,"10":2.5}; // cu in per conductor
  const calc = () => {
    const vol = BOX_VOL[boxType]; if(!vol) return;
    const n14=parseInt(w14)||0, n12=parseInt(w12)||0, n10=parseInt(w10)||0;
    const dev=parseInt(devices)||0, gnd=parseInt(grounds)||0, clmp=parseInt(clamps)||0;
    // NEC 314.16: each conductor counted, device=2x largest wire, grounds=1x largest, clamps=1x largest
    const largestVol = n10>0?2.5:n12>0?2.25:2;
    const totalVol = (n14*2)+(n12*2.25)+(n10*2.5)+(dev*2*largestVol)+(gnd>0?largestVol:0)+(clmp>0?largestVol:0);
    setResult({used:totalVol.toFixed(1),available:vol,ok:totalVol<=vol});
  };
  return (
    <div className="wp-card" data-testid="box-fill-calc">
      <h3 className="text-sm font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Box Fill (NEC 314.16)</h3>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="col-span-2"><label className="text-[10px] font-mono-data text-[#404040]">BOX TYPE</label>
          <select value={boxType} onChange={e=>setBoxType(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm">
            {Object.entries(BOX_VOL).map(([k,v])=><option key={k} value={k}>{k} ({v} cu in)</option>)}</select></div>
        {[["#14 WIRES",w14,setW14],["#12 WIRES",w12,setW12],["#10 WIRES",w10,setW10],["DEVICES",devices,setDevices],["GROUNDS",grounds,setGrounds],["CLAMPS",clamps,setClamps]].map(([l,v,s])=>(
          <div key={l}><label className="text-[10px] font-mono-data text-[#404040]">{l}</label>
          <input type="number" value={v} onChange={e=>s(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" /></div>
        ))}
      </div>
      <button onClick={calc} className="w-full py-2 text-xs font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#001D6C]">Check Fill</button>
      {result && <div className={`mt-2 p-2 rounded-sm text-center ${result.ok?"bg-[#34C75910] border border-[#34C759]":"bg-[#FF5A0010] border border-[#FF5A00]"}`}>
        <span className="font-mono font-bold text-lg">{result.used} cu in</span> <span className="font-mono-data text-[10px]">/ {result.available} cu in</span>
        <div className={`text-[10px] font-mono-data ${result.ok?"text-[#34C759]":"text-[#FF5A00]"}`}>{result.ok?"WITHIN BOX CAPACITY":"EXCEEDS - USE LARGER BOX"}</div>
      </div>}
    </div>
  );
}

export default function CalculatorsPage() {
  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="calculators-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Calculators
      </h1>
      <p className="text-xs text-[#404040] mb-2 font-mono-data">FIELD REFERENCE TOOLS</p>

      {/* Disclaimer */}
      <div className="p-2.5 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm mb-4">
        <p className="text-[10px] text-[#404040] leading-relaxed">
          <b>Disclaimer:</b> Calculator results are estimates for <b>reference only</b>. Always verify 
          calculations against the current NEC, IMC, and local code requirements. WorkPro assumes 
          no liability for errors in calculations or code interpretations. Consult a licensed 
          professional for final engineering decisions.
        </p>
      </div>

      <Tabs defaultValue="electrical">
        <TabsList className="grid w-full grid-cols-2 h-9 mb-4">
          <TabsTrigger value="electrical" className="text-xs flex items-center gap-1" data-testid="calc-electrical-tab">
            <Lightning size={12} /> Electrical
          </TabsTrigger>
          <TabsTrigger value="hvac" className="text-xs flex items-center gap-1" data-testid="calc-hvac-tab">
            <Flame size={12} /> HVAC
          </TabsTrigger>
        </TabsList>

        <TabsContent value="electrical">
          <div className="space-y-3">
            <OhmsLaw />
            <VoltageDrop />
            <ConduitFill />
            <BoxFill />
            <WireAmpacity />
          </div>
        </TabsContent>

        <TabsContent value="hvac">
          <div className="space-y-3">
            <BTUCalc />
            <DuctSizing />
            <RefrigerantChart />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
